/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.0.0
 */
import { RefObject, cloneElement, memo, useRef } from 'react';
import type { FC, KeyboardEvent, ReactElement } from 'react';

import classNames from 'classnames';
import VirtualList from 'react-tiny-virtual-list';
import { FixedSizeList } from 'react-window';

import InfinityScrollWrapper from '@/main/components/InfinityScrollWrapper/InfinityScrollWrapper';
import { usePreventWindowScroll } from '@/main/modules/app/hooks/usePreventWindowScroll';
import { TOverflow } from '@/main/types/globalTypes';
import { LoaderWrapperStyled } from '@components/common/MultiSelect/components/MultiSelectItemListStyled';
import { KeyboardKeyEnum } from '@modules/app/appConstants';
import type { ICommonTranslation } from '@modules/internationalization/types/index';
import { Loader } from '@modules/loader/components/Loader';

import { Checkbox, EllipseText, SelectItem } from '../../index';
import type { IMSOptionItemProps, ISelectVirtualListProps, MultiSelectItemProps } from '../MultiSelect';
import { FIXED_LIST_HEIGHT, VIRTUAL_ITEM_HEIGHT_LARGE, VIRTUAL_ITEM_HEIGHT_SMALL } from '../MultiSelectConstanst';
import { NotFindBlockStyled, SelectItemContainerStyled } from '../styled/MultiSelectStyled';
import StandardSelectList from './SelectItemContainer/StandardSelectList';

enum MultiSelectListTypeEnum {
    empty = 'empty',
    main = 'main',
    virtual = 'virtual',
    infinite = 'infinite',
}

export interface IMultiSelectItem {
    label: string;
    searchValue?: string;
}

export interface ISelectListVirtualListProps extends ISelectVirtualListProps {
    onScrollEvent: () => void;
}

type MSOptionItemType = MultiSelectItemProps & IMSOptionItemProps;

interface IMultiSelectItemListProps {
    firstUncheckedIndex?: number;
    isSuggest?: boolean;
    kind: string;
    options: MultiSelectItemProps[];
    searchValue: string;
    selectedOptions: MSOptionItemType[];
    suggestIsLoading?: boolean;
    tooltipOverflow?: TOverflow;
    tooltipMaxHeight?: string;
    translation: ICommonTranslation;
    onSelect: (item: MSOptionItemType) => void;
    render?: (props: MultiSelectItemProps) => ReactElement;
    virtualListProps?: ISelectListVirtualListProps;
}

type MultiSelectItemListType = MSOptionItemType & MultiSelectItemProps;

const MultiSelectItemList: FC<IMultiSelectItemListProps> = (props) => {
    const listBodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    usePreventWindowScroll(listBodyRef);

    const keyDownHandler = (event: KeyboardEvent, item: IMSOptionItemProps) => {
        if (event.key === KeyboardKeyEnum.ENTER) {
            event.stopPropagation();
            props.onSelect(item);
        }
    };
    const enrichOptions: MultiSelectItemListType[] = props.options.map((item: MSOptionItemType) => {
        item.checked = props.selectedOptions.some((i) => i.value === item.value);
        return item;
    });
    const showEmpty = !props.options.length;
    const emptyText = props.isSuggest
        ? props.translation.multiSelect.startSearch
        : props.translation.multiSelect.foundNothing;

    let status = MultiSelectListTypeEnum.main;

    if (showEmpty) {
        status = MultiSelectListTypeEnum.empty;
    }
    if (!showEmpty && !!props.virtualListProps) {
        status = MultiSelectListTypeEnum.infinite;
    }
    if (!showEmpty && !props.virtualListProps && enrichOptions.length > 20) {
        status = MultiSelectListTypeEnum.virtual;
    }

    const renderListItem = (item: MultiSelectItemListType): ReactElement => {
        if (props.render) {
            return props.render(item);
        }
        return (
            <StandardSelectList
                {...item}
                tooltipMaxHeight={props.tooltipMaxHeight}
                tooltipOverflow={props.tooltipOverflow}
            />
        );
    };

    if (props.suggestIsLoading) {
        return (
            <LoaderWrapperStyled>
                <Loader showLoader={true} />
            </LoaderWrapperStyled>
        );
    }

    return (
        <>
            {
                {
                    empty: (
                        <ul className="multiselect__select-list list">
                            <NotFindBlockStyled>
                                {!props.suggestIsLoading && (
                                    <EllipseText title={props.searchValue}>
                                        {props.searchValue
                                            ? `${props.translation.multiSelect.notFound} "${props.searchValue}"`
                                            : emptyText}
                                    </EllipseText>
                                )}
                            </NotFindBlockStyled>
                        </ul>
                    ),
                    infinite: !!props.virtualListProps && (
                        <InfinityScrollWrapper
                            hasMore={props.virtualListProps.hasMore}
                            isLoading={props.virtualListProps.isLoading}
                            sizeElement={listBodyRef}
                            watchElement={listBodyRef}
                            onScrollEvent={props.virtualListProps.onScrollEvent}
                        >
                            <FixedSizeList
                                className="select__select-list list virtual"
                                height={FIXED_LIST_HEIGHT}
                                itemCount={props.options.length}
                                itemSize={props.virtualListProps.rowHeight}
                                outerRef={listBodyRef}
                                width="100%"
                            >
                                {({ index, style }) => (
                                    <SelectItemContainerStyled
                                        className={classNames('infinite', {
                                            'first-unchecked': index === props.firstUncheckedIndex,
                                        })}
                                        key={enrichOptions[index].value}
                                        name={enrichOptions[index].value.toString()}
                                        style={style}
                                    >
                                        <SelectItem
                                            className="multiselect__select-item multiselect-item"
                                            onClick={() => props.onSelect(enrichOptions[index])}
                                            onKeyDown={(e: KeyboardEvent) => keyDownHandler(e, enrichOptions[index])}
                                        >
                                            <Checkbox
                                                checked={!!enrichOptions[index].checked}
                                                className="multiselect-item__checkbox"
                                                kind={props.kind}
                                            />
                                            {cloneElement(renderListItem(enrichOptions[index]), {
                                                searchValue: props.searchValue,
                                            })}
                                        </SelectItem>
                                    </SelectItemContainerStyled>
                                )}
                            </FixedSizeList>
                        </InfinityScrollWrapper>
                    ),
                    virtual: (
                        <VirtualList
                            className="select__select-list list virtual"
                            height={FIXED_LIST_HEIGHT}
                            itemCount={enrichOptions.length}
                            itemSize={(index) =>
                                index === props.firstUncheckedIndex
                                    ? VIRTUAL_ITEM_HEIGHT_LARGE
                                    : VIRTUAL_ITEM_HEIGHT_SMALL
                            }
                            renderItem={({ index, style }) => (
                                <SelectItemContainerStyled
                                    className={classNames({ 'first-unchecked': index === props.firstUncheckedIndex })}
                                    key={enrichOptions[index].value}
                                    name={enrichOptions[index].value.toString()}
                                    style={style}
                                >
                                    <SelectItem
                                        className="multiselect__select-item multiselect-item"
                                        onClick={() => props.onSelect(enrichOptions[index])}
                                        onKeyDown={(e: KeyboardEvent) => keyDownHandler(e, enrichOptions[index])}
                                    >
                                        <Checkbox
                                            checked={!!enrichOptions[index].checked}
                                            className="multiselect-item__checkbox"
                                            kind={props.kind}
                                        />
                                        {cloneElement(renderListItem(enrichOptions[index]), {
                                            searchValue: props.searchValue,
                                        })}
                                    </SelectItem>
                                </SelectItemContainerStyled>
                            )}
                            width="100%"
                        />
                    ),
                    main: (
                        <ul className="multiselect__select-list list">
                            {enrichOptions.map((item: MultiSelectItemListType, index: number) => (
                                <SelectItemContainerStyled
                                    className={classNames({ 'first-unchecked': index === props.firstUncheckedIndex })}
                                    key={item.value}
                                    name={item.value.toString()}
                                >
                                    <SelectItem
                                        className="multiselect__select-item multiselect-item"
                                        onClick={() => props.onSelect(item)}
                                        onKeyDown={(e: KeyboardEvent) => keyDownHandler(e, item)}
                                    >
                                        <Checkbox
                                            checked={!!item.checked}
                                            className="multiselect-item__checkbox"
                                            kind={props.kind}
                                        />
                                        {cloneElement(renderListItem(item), {
                                            searchValue: props.searchValue,
                                        })}
                                    </SelectItem>
                                </SelectItemContainerStyled>
                            ))}
                        </ul>
                    ),
                }[status]
            }
        </>
    );
};

export default memo(MultiSelectItemList);
